import React, { useState, useContext, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Card, ProgressBar, Badge, Table, Button, Form, Modal, Alert, Spinner } from "react-bootstrap";
import { FloatCardComponent } from "../../components/cards";
import PageLayout from "../../layouts/PageLayout";
import { getFondos, crearFondo, actualizarFondo, eliminarFondo, toggleEstadoFondo } from "../../api/gastos.api.ts";
import Swal from 'sweetalert2';

export default function FondosPresupuestoPage() {
    const { t } = useContext(TranslatorContext);
    
    // Estado para el año seleccionado
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Estado para modales
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(''); // 'create', 'edit', 'delete'
    const [selectedFondo, setSelectedFondo] = useState(null);
    
    // Estado para loading y errores
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Estado para formulario
    const [formData, setFormData] = useState({
        fon_codigo: '',
        fon_nombre: '',
        fon_descripcion: '',
        fon_presupuesto_anual: '',
        fon_activo: true
    });

    // Estado para fondos
    const [fondos, setFondos] = useState([]);

    // Cargar fondos desde la API
    const cargarFondos = async (anio) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getFondos(anio);
            if (response.success) {
                setFondos(response.data);
            } else {
                setError('Error al cargar los fondos');
            }
        } catch (error) {
            setError('Error al conectar con el servidor');
            console.error('Error al cargar fondos:', error);
        } finally {
            setLoading(false);
        }
    };

    // Cargar fondos al cambiar el año
    useEffect(() => {
        cargarFondos(selectedYear);
    }, [selectedYear]);

    // Generar años disponibles
    const generarAniosDisponibles = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            years.push(i);
        }
        return years;
    };

    // Resumen general
    const totalPresupuesto = fondos.reduce((sum, fondo) => sum + (parseFloat(fondo.fon_presupuesto_anual) || 0), 0);
    const totalGastado = 0; // Aquí se podría calcular desde los gastos
    const totalDisponible = totalPresupuesto - totalGastado;

    const floats = [
        { 
            "title": "Presupuesto Total", 
            "digit": totalPresupuesto, 
            "icon": "account_balance_wallet", 
            "variant": "lg blue" 
        }, 
        { 
            "title": "Total Ejecutado", 
            "digit": totalGastado, 
            "icon": "trending_down", 
            "variant": "lg red" 
        },
        { 
            "title": "Saldo Disponible", 
            "digit": totalDisponible, 
            "icon": "savings", 
            "variant": "lg green" 
        },
        { 
            "title": "Fondos Activos", 
            "digit": fondos.filter(f => f.fon_activo).length, 
            "icon": "folder", 
            "variant": "lg purple" 
        }
    ];

    const getStatusBadge = (porcentaje) => {
        if (porcentaje < 50) return { variant: "success", text: "Bajo" };
        if (porcentaje < 80) return { variant: "warning", text: "Medio" };
        return { variant: "danger", text: "Alto" };
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    // Funciones para manejar el modal
    const abrirModal = (tipo, fondo = null) => {
        setModalType(tipo);
        setSelectedFondo(fondo);
        if (fondo) {
            setFormData({
                fon_codigo: fondo.fon_codigo,
                fon_nombre: fondo.fon_nombre,
                fon_descripcion: fondo.fon_descripcion || '',
                fon_presupuesto_anual: fondo.fon_presupuesto_anual.toString(),
                fon_activo: fondo.fon_activo
            });
        } else {
            setFormData({
                fon_codigo: '',
                fon_nombre: '',
                fon_descripcion: '',
                fon_presupuesto_anual: '',
                fon_activo: true
            });
        }
        setShowModal(true);
    };

    const cerrarModal = () => {
        setShowModal(false);
        setModalType('');
        setSelectedFondo(null);
        setFormData({
            fon_codigo: '',
            fon_nombre: '',
            fon_descripcion: '',
            fon_presupuesto_anual: '',
            fon_activo: true
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const guardarFondo = async () => {
        setLoading(true);
        try {
            const fondoData = {
                fon_codigo: formData.fon_codigo,
                fon_nombre: formData.fon_nombre,
                fon_descripcion: formData.fon_descripcion,
                fon_presupuesto_anual: parseFloat(formData.fon_presupuesto_anual),
                fon_anio: selectedYear,
                fon_activo: formData.fon_activo
            };

            let response;
            if (modalType === 'create') {
                response = await crearFondo(fondoData);
            } else if (modalType === 'edit') {
                response = await actualizarFondo(selectedFondo.fon_id, fondoData);
            }

            if (response.success) {
                Swal.fire({
                    title: 'Éxito!',
                    text: response.message || 'Operación realizada correctamente',
                    icon: 'success',
                    confirmButtonText: 'OK'
                });
                cargarFondos(selectedYear);
                cerrarModal();
            } else {
                Swal.fire({
                    title: 'Error!',
                    text: response.message || 'Ocurrió un error',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Error!',
                text: 'Error al conectar con el servidor',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const confirmarEliminarFondo = (fondo) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas eliminar el fondo "${fondo.fon_nombre}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const response = await eliminarFondo(fondo.fon_id);
                    if (response.success) {
                        Swal.fire('Eliminado!', response.message, 'success');
                        cargarFondos(selectedYear);
                    } else {
                        Swal.fire('Error!', response.message, 'error');
                    }
                } catch (error) {
                    Swal.fire('Error!', 'Error al conectar con el servidor', 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const toggleEstadoFondoAPI = async (fondo) => {
        setLoading(true);
        try {
            const response = await toggleEstadoFondo(fondo.fon_id);
            if (response.success) {
                cargarFondos(selectedYear);
            } else {
                Swal.fire('Error!', response.message, 'error');
            }
        } catch (error) {
            Swal.fire('Error!', 'Error al conectar con el servidor', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Mantenedor de Fondos</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Fondos</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Selector de Año y Controles */}
                <Col xl={12}>
                    <Card className="mc-card mb-4">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">Año de Gestión</Form.Label>
                                        <Form.Select 
                                            value={selectedYear} 
                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            className="form-select-lg"
                                        >
                                            {generarAniosDisponibles().map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <div className="text-center">
                                        <h5 className="text-muted mb-1">Gestión Presupuestaria {selectedYear}</h5>
                                        <p className="mb-0">Total de {fondos.length} fondos configurados</p>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="d-grid gap-2">
                                        <Button 
                                            variant="primary" 
                                            onClick={() => abrirModal('create')}
                                        >
                                            <i className="material-icons me-2">add</i>
                                            Crear Fondo
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Cards de resumen */}
                {floats.map((float, index) => (
                    <Col key={index} sm={6} lg={3}>
                        <FloatCardComponent 
                            variant={float.variant}
                            digit={typeof float.digit === 'number' ? formatCurrency(float.digit) : float.digit}
                            title={float.title}
                            icon={float.icon}
                        />
                    </Col>
                ))}

                {/* Detalle de fondos */}
                <Col xl={12}>
                    <Card className="mc-card">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4>Fondos Configurados - {selectedYear}</h4>
                            <div>
                                <Button variant="outline-success" size="sm" className="me-2">
                                    <i className="material-icons me-1">download</i>
                                    Exportar
                                </Button>
                                <Button variant="outline-primary" size="sm">
                                    <i className="material-icons me-1">settings</i>
                                    Configurar
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {/* Mostrar error si existe */}
                            {error && (
                                <Alert variant="danger" className="mb-3">
                                    <i className="material-icons me-2">error</i>
                                    {error}
                                </Alert>
                            )}

                            {/* Mostrar loading */}
                            {loading && (
                                <div className="text-center py-4">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </Spinner>
                                    <p className="mt-2">Cargando fondos...</p>
                                </div>
                            )}

                            <Row>
                                {!loading && fondos.length === 0 ? (
                                    <Col xl={12}>
                                        <div className="text-center py-5">
                                            <i className="material-icons text-muted" style={{fontSize: '48px'}}>folder_open</i>
                                            <h5 className="text-muted mt-3">No hay fondos configurados para {selectedYear}</h5>
                                            <p className="text-muted">Crea tu primer fondo para comenzar a gestionar el presupuesto</p>
                                            <Button 
                                                variant="primary" 
                                                onClick={() => abrirModal('create')}
                                            >
                                                <i className="material-icons me-2">add</i>
                                                Crear Primer Fondo
                                            </Button>
                                        </div>
                                    </Col>
                                ) : (
                                    !loading && fondos.map((fondo, index) => {
                                        const porcentajeUsado = 0; // Se calculará desde gastos reales
                                        const saldoDisponible = fondo.fon_presupuesto_anual;
                                        const status = getStatusBadge(porcentajeUsado);

                                        return (
                                            <Col key={fondo.fon_id} md={6} lg={6} className="mb-4">
                                                <Card style={{ 
                                                    borderLeft: `4px solid #007bff`, 
                                                    opacity: fondo.fon_activo ? 1 : 0.6 
                                                }}>
                                                    <Card.Body>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <div>
                                                                <h5 className="mb-0">{fondo.fon_nombre}</h5>
                                                                <small className="text-muted">{fondo.fon_descripcion}</small>
                                                            </div>
                                                            <div className="d-flex align-items-center">
                                                                <Badge bg={status.variant} className="me-2">{status.text}</Badge>
                                                                <Badge bg={fondo.fon_activo ? "success" : "secondary"}>
                                                                    {fondo.fon_activo ? "Activo" : "Inactivo"}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="mb-3">
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Código:</span>
                                                                <strong>{fondo.fon_codigo}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Presupuesto:</span>
                                                                <strong>{formatCurrency(fondo.fon_presupuesto_anual)}</strong>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span>Ejecutado:</span>
                                                                <span style={{ color: '#007bff' }}>
                                                                    {formatCurrency(0)}
                                                                </span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>Disponible:</span>
                                                                <strong className="text-success">
                                                                    {formatCurrency(saldoDisponible)}
                                                                </strong>
                                                            </div>
                                                        </div>

                                                        <ProgressBar 
                                                            now={porcentajeUsado} 
                                                            variant={porcentajeUsado > 80 ? "danger" : porcentajeUsado > 60 ? "warning" : "success"}
                                                            style={{ height: '10px' }}
                                                        />
                                                        <div className="d-flex justify-content-between mt-2">
                                                            <small className="text-muted">{porcentajeUsado.toFixed(1)}% usado</small>
                                                            <small className="text-muted">0 gastos</small>
                                                        </div>

                                                        {/* Botones de acción */}
                                                        <div className="d-flex justify-content-between mt-3">
                                                            <div>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm" 
                                                                    className="me-2"
                                                                    onClick={() => abrirModal('edit', fondo)}
                                                                >
                                                                    <i className="material-icons">edit</i>
                                                                </Button>
                                                                <Button 
                                                                    variant={fondo.fon_activo ? "outline-warning" : "outline-success"} 
                                                                    size="sm"
                                                                    className="me-2"
                                                                    onClick={() => toggleEstadoFondoAPI(fondo)}
                                                                >
                                                                    <i className="material-icons">
                                                                        {fondo.fon_activo ? "visibility_off" : "visibility"}
                                                                    </i>
                                                                </Button>
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm"
                                                                    onClick={() => confirmarEliminarFondo(fondo)}
                                                                >
                                                                    <i className="material-icons">delete</i>
                                                                </Button>
                                                            </div>
                                                            <Button variant="outline-info" size="sm">
                                                                <i className="material-icons me-1">analytics</i>
                                                                Ver Detalle
                                                            </Button>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    })
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Tabla resumen de fondos */}
                {fondos.length > 0 && (
                    <Col xl={12}>
                        <Card className="mc-card">
                            <Card.Header>
                                <h4>Resumen de Fondos - {selectedYear}</h4>
                            </Card.Header>
                            <Card.Body>
                                <Table responsive striped>
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Fondo Asignado</th>
                                            <th>Presupuesto</th>
                                            <th>Ejecutado</th>
                                            <th>Disponible</th>
                                            <th>% Ejecución</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fondos.filter(f => f.fon_activo).map((fondo, index) => {
                                            const porcentajeUsado = 0; // Se calculará desde gastos reales
                                            const saldoDisponible = fondo.fon_presupuesto_anual;
                                            const status = getStatusBadge(porcentajeUsado);

                                            return (
                                                <tr key={fondo.fon_id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div 
                                                                className="me-2" 
                                                                style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    backgroundColor: '#007bff',
                                                                    borderRadius: '2px'
                                                                }}
                                                            ></div>
                                                            {fondo.fon_codigo}
                                                        </div>
                                                    </td>
                                                    <td>{fondo.fon_nombre}</td>
                                                    <td>{formatCurrency(fondo.fon_presupuesto_anual)}</td>
                                                    <td style={{ color: '#007bff' }}>
                                                        {formatCurrency(0)}
                                                    </td>
                                                    <td className="text-success">
                                                        {formatCurrency(saldoDisponible)}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div style={{ width: '60px', marginRight: '8px' }}>
                                                                <ProgressBar 
                                                                    now={porcentajeUsado} 
                                                                    style={{ height: '6px' }}
                                                                    variant={porcentajeUsado > 80 ? "danger" : porcentajeUsado > 60 ? "warning" : "success"}
                                                                />
                                                            </div>
                                                            <small>{porcentajeUsado.toFixed(1)}%</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg={status.variant}>{status.text}</Badge>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Modal para crear/editar fondos */}
            <Modal show={showModal} onHide={cerrarModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalType === 'create' ? 'Crear Nuevo Fondo' : 'Editar Fondo'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Código del Fondo *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="fon_codigo"
                                        value={formData.fon_codigo}
                                        onChange={handleInputChange}
                                        placeholder="Ej: FON-001"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nombre del Fondo *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="fon_nombre"
                                        value={formData.fon_nombre}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Fondo Emergencia"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="fon_descripcion"
                                        value={formData.fon_descripcion}
                                        onChange={handleInputChange}
                                        placeholder="Descripción del propósito del fondo"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Presupuesto Anual *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="fon_presupuesto_anual"
                                        value={formData.fon_presupuesto_anual}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="fon_activo"
                                        checked={formData.fon_activo}
                                        onChange={handleInputChange}
                                        label="Fondo activo"
                                        className="mt-4"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cerrarModal}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={guardarFondo}
                        disabled={!formData.fon_nombre || !formData.fon_codigo || !formData.fon_presupuesto_anual || loading}
                    >
                        {loading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Procesando...
                            </>
                        ) : (
                            modalType === 'create' ? 'Crear Fondo' : 'Guardar Cambios'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}
